// Review → Agreement: upload the firm's agreement PDF, send it (firm signs, then the Vertekal countersigner),
// follow each signer, remind, void or replace, countersign, download the original and the signed copy, read
// the audit trail. Signer states and the progress line come from @upm/domain (ported from the portal).
import { useCallback, useEffect, useRef, useState } from 'react';
import { Download, FileText, Upload } from 'lucide-react';
import {
  AGREEMENT_MAX_BYTES, AGREEMENT_STATUS_LABEL, agreementProgressLabel, isActiveAgreement, SIGNER_STATE_LABEL,
  type AgreementView, type Review, type SendAgreementRequest, type StaffMember, type VoidAgreementRequest,
} from '@upm/domain';
import { api, ApiError } from '../lib/api';
import { firstName, fmtDate, fmtDateTime } from '../lib/format';
import { Banner, Button, Dialog, Pill, TableBox, Td, TextArea, Th, cx } from '../ui';
import { SignatureForm, type SignatureValue } from '../agreement/SignatureForm';
import { fmtSize } from '../agreement/SignAgreement';
import { useMe } from './AdminShell';
import { agreementTone } from './labels';

const msg = (e: unknown, fallback: string) => (e instanceof ApiError && e.code !== 'network' ? e.message : fallback);

async function openDownload(a: AgreementView, kind: 'original' | 'final', setErr: (s: string | null) => void) {
  setErr(null);
  const win = window.open('', '_blank');
  try {
    const res = await api.get<{ url: string }>(`/api/admin/agreements/${a.id}/download?kind=${kind}`);
    if (win) { win.opener = null; win.location.href = res.url; } else window.location.href = res.url;
  } catch (e) {
    win?.close();
    setErr(msg(e, 'Could not download the PDF.'));
  }
}

export function AgreementCard({ r, reload }: { r: Review; reload: () => Promise<void> }) {
  const active = r.agreements.find((a) => isActiveAgreement(a.status)) ?? null;
  const history = r.agreements.filter((a) => a !== active);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const closed = r.status === 'withdrawn' || r.status === 'handed_off';

  // A fresh upload or signed copy is scanned before it can be sent or downloaded.
  const scanning = !!active && (active.scan === 'pending' || active.final?.scan === 'pending');
  useEffect(() => {
    if (!scanning) return;
    const t = setInterval(() => { void reload(); }, 5000);
    return () => clearInterval(t);
  }, [scanning, reload]);

  const run = useCallback(async (p: Promise<unknown>, done: string) => {
    setErr(null);
    setNotice(null);
    try { await p; setNotice(done); await reload(); return true; }
    catch (e) { setErr(msg(e, 'That didn’t save. Try again.')); return false; }
  }, [reload]);

  return (
    <section aria-labelledby="agreement-h" className="flex flex-col gap-3 rounded-card border border-line bg-white p-[18px]">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 id="agreement-h" className="m-0 text-[15px] font-semibold">Agreement</h3>
        {active && <span className="flex flex-wrap items-center gap-2 text-[13px] text-muted"><Pill tone={agreementTone(active.status)} dot>{AGREEMENT_STATUS_LABEL[active.status]}</Pill>{agreementProgressLabel(active)}</span>}
      </div>
      {notice && <Banner tone="success" role="status">{notice}</Banner>}
      {err && <Banner tone="error" role="alert">{err}</Banner>}
      {active
        ? <ActiveAgreement a={active} onboardingId={r.id} run={run} setErr={setErr} closed={closed} held={r.kickoffHeldAt !== null} contact={r.header.inviteName} />
        : <UploadForm onboardingId={r.id} disabled={closed} onDone={async () => { setNotice('Agreement uploaded. Pick the countersigner and send it.'); await reload(); }} setErr={setErr}
            hint={history[0]?.status === 'declined' ? `The firm declined the last one: “${history[0].declineReason ?? ''}”` : undefined} />}
      {history.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer font-semibold text-ink-2">Earlier agreements ({history.length})</summary>
          <ul className="m-0 mt-2 flex list-none flex-col gap-2 p-0">
            {history.map((a) => (
              <li key={a.id} className="rounded-lg bg-panel px-3 py-2">
                <div className="flex flex-wrap items-center gap-2"><span className="font-semibold [overflow-wrap:anywhere]">{a.fileName}</span><Pill tone={agreementTone(a.status)}>{AGREEMENT_STATUS_LABEL[a.status]}</Pill></div>
                <div className="text-[13px] text-muted">
                  {a.status === 'voided' ? `Voided ${a.voidedAt ? fmtDate(a.voidedAt) : ''}${a.voidedBy ? ` by ${a.voidedBy}` : ''}: ${a.voidReason ?? ''}` : `Declined ${a.declinedAt ? fmtDate(a.declinedAt) : ''}: “${a.declineReason ?? ''}”`}
                </div>
                <AuditTrail a={a} />
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function UploadForm({ onboardingId, replaceId, disabled, onDone, setErr, hint }: {
  onboardingId: string; replaceId?: string; disabled?: boolean; onDone: () => Promise<void>; setErr: (s: string | null) => void; hint?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const pick = async (f: File | undefined) => {
    if (!f) return;
    setErr(null);
    if (!/\.pdf$/i.test(f.name)) { setErr('Upload the agreement as a PDF.'); return; }
    if (f.size > AGREEMENT_MAX_BYTES) { setErr(`The agreement can be up to ${AGREEMENT_MAX_BYTES / 1024 / 1024} MB.`); return; }
    const form = new FormData();
    if (replaceId) form.append('replaceId', replaceId);
    form.append('file', f);
    setBusy(true);
    try { await api.post(`/api/admin/onboardings/${onboardingId}/agreements`, form); await onDone(); }
    catch (e) { setErr(msg(e, 'Could not upload the PDF.')); }
    finally { setBusy(false); if (input.current) input.current.value = ''; }
  };
  return (
    <div className="flex flex-col gap-2 text-sm">
      {!replaceId && <p className="m-0 text-ink-2">Upload this firm’s agreement as a PDF (up to {AGREEMENT_MAX_BYTES / 1024 / 1024} MB). The firm signs it on its status page, then the countersigner you pick signs for Vertekal.</p>}
      {hint && <p className="m-0 text-[13px] text-warn-fg">{hint}</p>}
      <input ref={input} id={`agreement-file-${replaceId ?? 'new'}`} type="file" accept="application/pdf,.pdf" className="sr-only" onChange={(e) => void pick(e.target.files?.[0])} aria-label={replaceId ? 'Replacement agreement PDF' : 'Agreement PDF'} />
      <div><Button variant="outline" size="sm" disabled={disabled || busy} onClick={() => input.current?.click()}><Upload aria-hidden="true" size={16} />{busy ? 'Uploading…' : replaceId ? 'Choose the new PDF' : 'Upload the agreement PDF'}</Button></div>
    </div>
  );
}

type Run = (p: Promise<unknown>, done: string) => Promise<boolean>;

/** `contact`: the invite contact, who signs for the firm and gets the agreement emails. */
function ActiveAgreement({ a, onboardingId, run, setErr, closed, held, contact }: { a: AgreementView; onboardingId: string; run: Run; setErr: (s: string | null) => void; closed: boolean; held: boolean; contact: string }) {
  const { me } = useMe();
  const [dialog, setDialog] = useState<null | 'void' | 'replace' | 'countersign'>(null);
  const [reason, setReason] = useState('');
  const [team, setTeam] = useState<StaffMember[] | null>(null);
  const [countersigner, setCountersigner] = useState('');
  const [busy, setBusy] = useState(false);
  const [signErr, setSignErr] = useState<string | null>(null);

  useEffect(() => {
    if (a.status !== 'draft') return;
    api.get<StaffMember[]>('/api/admin/staff').then((t) => setTeam(t.filter((m) => m.status === 'active')), () => setTeam([]));
  }, [a.status]);
  useEffect(() => { if (!countersigner && me?.email) setCountersigner(me.email.toLowerCase()); }, [me, countersigner]);

  const cs = a.signers.find((s) => s.role === 'countersigner');
  const isCountersigner = !!cs && !!me && cs.email.toLowerCase() === me.email.toLowerCase();
  const voidable = a.status === 'draft' || a.status === 'sent' || a.status === 'firm_signed';
  const options = team && team.length ? team : me ? [{ email: me.email, name: me.name }] : [];

  const countersign = async (v: SignatureValue) => {
    setBusy(true);
    setSignErr(null);
    try {
      await api.post(`/api/admin/agreements/${a.id}/countersign`, { ...v, documentSha256: a.originalSha256 });
      setDialog(null);
      await run(Promise.resolve(), 'Countersigned. The signed PDF is on its way to the firm.');
    } catch (e) { setSignErr(msg(e, 'Could not record your signature.')); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col gap-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-panel px-3 py-2">
        <div className="flex min-w-0 items-center gap-2.5">
          <FileText aria-hidden="true" size={20} className="flex-none text-brand" />
          <div className="min-w-0">
            <div className="font-semibold [overflow-wrap:anywhere]">{a.fileName}</div>
            <div className="text-[13px] text-muted">
              {a.pageCount} {a.pageCount === 1 ? 'page' : 'pages'} · {fmtSize(a.sizeBytes)} · Uploaded {fmtDate(a.createdAt)} by {a.createdBy}
              {a.scan === 'pending' && ' · Scanning…'}{a.scan === 'rejected' && ' · Rejected by the malware scan'}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-1">
          <Button variant="link" size="sm" disabled={a.scan !== 'clean'} onClick={() => void openDownload(a, 'original', setErr)}><Download aria-hidden="true" size={14} />Original</Button>
          {a.final && <Button variant="link" size="sm" disabled={a.final.scan !== 'clean'} title={a.final.scan === 'pending' ? 'Scanning the signed copy' : undefined} onClick={() => void openDownload(a, 'final', setErr)}><Download aria-hidden="true" size={14} />Signed PDF</Button>}
        </div>
      </div>

      {a.status === 'draft' ? (
        <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); void run(api.post(`/api/admin/agreements/${a.id}/send`, { countersignerEmail: countersigner } satisfies SendAgreementRequest), 'Sent. The firm has an email with a Review and sign button.'); }}>
          <label className="flex min-w-[220px] flex-1 flex-col gap-1.5">
            <span className="font-semibold">Vertekal countersigner</span>
            <select className="input" value={countersigner} onChange={(e) => setCountersigner(e.target.value)}>
              {options.map((m) => <option key={m.email} value={m.email.toLowerCase()}>{m.name || m.email} ({m.email})</option>)}
            </select>
          </label>
          <Button type="submit" variant="primary" size="md" aria-describedby={held ? undefined : `${a.id}-held`} disabled={closed || !held || a.scan !== 'clean' || !countersigner}>{a.scan === 'pending' ? 'Scanning…' : 'Send for signature'}</Button>
          <Button variant="quiet" size="sm" onClick={() => { setReason('Discarded draft'); setDialog('void'); }}>Discard</Button>
          {/* The firm sees the agreement only after the Discovery Session, the way uploads wait for the signed agreement. */}
          {held && !closed && <p className="m-0 basis-full text-[13px] text-muted">Emails {firstName(contact)} a Review and sign link.</p>}
          {!held && !closed && (
            <p id={`${a.id}-held`} className="m-0 basis-full text-[13px] text-muted">You can send the agreement once the Discovery Session gate is closed. After the call, finish its tasks and choose <b>Close Discovery Session</b> on the Gates tab.</p>
          )}
        </form>
      ) : (
        <ol className="m-0 flex list-none flex-col gap-2 p-0" aria-label="Signers">
          {a.signers.map((s) => (
            <li key={s.id} className="flex flex-wrap items-start justify-between gap-2 border-b border-line-soft pb-2 last:border-b-0">
              <div className="min-w-0">
                <div className="font-semibold">{s.order}. {s.name} <span className="font-normal text-muted">· {s.role === 'firm' ? 'Firm' : 'Vertekal countersigner'}</span></div>
                <div className="text-[13px] text-muted [overflow-wrap:anywhere]">
                  {s.email}
                  {s.signedAt && ` · Signed ${fmtDateTime(s.signedAt)} as ${s.signedName}${s.signedTitle ? `, ${s.signedTitle}` : ''}`}
                  {!s.signedAt && s.viewedAt && ` · Viewed ${fmtDateTime(s.viewedAt)}`}
                  {s.declinedAt && ` · Declined ${fmtDateTime(s.declinedAt)}: “${s.declineReason ?? ''}”`}
                  {s.reminderCount > 0 && ` · Reminded ${s.reminderCount}×${s.lastRemindedAt ? `, last ${fmtDate(s.lastRemindedAt)}` : ''}`}
                </div>
              </div>
              <Pill tone={s.state === 'signed' ? 'ok' : s.state === 'declined' ? 'bad' : s.state === 'waiting' || s.state === 'voided' ? 'neutral' : 'info'}>{SIGNER_STATE_LABEL[s.state]}</Pill>
            </li>
          ))}
        </ol>
      )}

      {a.status === 'executed' && a.final && (
        <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-[13px]">
          <dt className="text-muted">Original SHA-256</dt><dd className="m-0 font-mono [overflow-wrap:anywhere]">{a.originalSha256}</dd>
          <dt className="text-muted">Signed SHA-256</dt><dd className="m-0 font-mono [overflow-wrap:anywhere]">{a.final.sha256}</dd>
        </dl>
      )}

      {a.status !== 'draft' && (
        <div className="flex flex-wrap gap-2">
          {isCountersigner && a.status === 'firm_signed' && <Button variant="primary" size="sm" onClick={() => { setSignErr(null); setDialog('countersign'); }}>Countersign</Button>}
          {!isCountersigner && a.status === 'firm_signed' && cs && <span className="self-center text-[13px] text-muted">Waiting on {cs.name} to countersign.</span>}
          {a.status === 'sent' && <Button variant="outline" size="sm" onClick={() => void run(api.post(`/api/admin/agreements/${a.id}/remind`), 'Reminder emailed to the firm.')}>Remind the firm</Button>}
          {a.status === 'sent' && <span className="self-center text-[13px] text-muted">Emails {firstName(a.signers.find((s) => s.role === 'firm')?.name ?? contact)} a reminder.</span>}
          {voidable && <Button variant="secondary" size="sm" onClick={() => setDialog('replace')}>Replace</Button>}
          {voidable && <Button variant="quiet" size="sm" onClick={() => { setReason(''); setDialog('void'); }}>Void</Button>}
        </div>
      )}
      <AuditTrail a={a} />

      <Dialog open={dialog === 'void'} title="Void this agreement?" onClose={() => setDialog(null)}
        footer={<><Button onClick={() => setDialog(null)}>Cancel</Button><Button variant="danger" disabled={!reason.trim()} onClick={async () => { if (await run(api.post(`/api/admin/agreements/${a.id}/void`, { reason: reason.trim() } satisfies VoidAgreementRequest), 'Agreement voided. It stays in the history.')) setDialog(null); }}>Void</Button></>}>
        <p className="m-0 text-ink-2">Nobody can sign it after this. The firm no longer sees it, and it stays in this firm’s agreement history.</p>
        <TextArea label="Reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Dialog>
      <Dialog open={dialog === 'replace'} title="Replace this agreement?" onClose={() => setDialog(null)} footer={<Button onClick={() => setDialog(null)}>Cancel</Button>}>
        <p className="m-0 text-ink-2">The current agreement is voided as soon as the new PDF uploads. Send the new one when it has been scanned.</p>
        <UploadForm onboardingId={onboardingId} replaceId={a.id} setErr={setErr} onDone={async () => { setDialog(null); await run(Promise.resolve(), 'New agreement uploaded; the previous one is voided. Send it when ready.'); }} />
      </Dialog>
      <Dialog open={dialog === 'countersign'} title="Countersign for Vertekal" wide onClose={() => setDialog(null)}>
        <p className="m-0 text-ink-2">
          {a.signers[0]?.signedName} signed for the firm{a.signers[0]?.signedAt ? ` on ${fmtDateTime(a.signers[0].signedAt)}` : ''}.
          {' '}<button type="button" className="border-0 bg-transparent p-0 font-semibold text-brand underline" onClick={() => void openDownload(a, 'original', setErr)}>Read the agreement</button> before you sign.
        </p>
        <SignatureForm defaultName={cs?.name} busy={busy} error={signErr} signLabel="Countersign" onSign={(v) => void countersign(v)}
          secondary={<Button variant="quiet" onClick={() => setDialog(null)}>Cancel</Button>} />
      </Dialog>
    </div>
  );
}

function AuditTrail({ a }: { a: AgreementView }) {
  if (!a.audit.length) return null;
  return (
    <details className="text-sm">
      <summary className="cursor-pointer font-semibold text-ink-2">Audit trail ({a.audit.length})</summary>
      <div className="mt-2">
        <TableBox minWidth={560} label="Agreement audit trail">
          <thead><tr className="border-b border-line-soft"><Th>When</Th><Th>Who</Th><Th>What</Th><Th>IP</Th></tr></thead>
          <tbody>
            {a.audit.map((e, i) => (
              <tr key={`${e.at}-${i}`} className={cx('border-b border-line-soft last:border-b-0')}>
                <Td className="whitespace-nowrap text-muted">{fmtDateTime(e.at)}</Td>
                <Td>{e.actor}</Td>
                <Td><span title={e.userAgent ?? undefined}>{e.summary}</span></Td>
                <Td className="text-muted">{e.ip ?? ''}</Td>
              </tr>
            ))}
          </tbody>
        </TableBox>
      </div>
    </details>
  );
}
